import { useState, useEffect } from 'react';

function TrainingProgress({ clone, estimatedTime, onRefresh }) {
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showDetails, setShowDetails] = useState(false);

  // Calculate initial elapsed time from training_started_at
  useEffect(() => {
    if (clone?.training_status !== 'training') {
      setElapsedTime(0);
      return;
    }

    // Calculate elapsed from server timestamp
    const calculateElapsed = () => {
      if (clone?.training_started_at) {
        const startTime = new Date(clone.training_started_at).getTime();
        const now = Date.now();
        return Math.floor((now - startTime) / 1000);
      }
      return 0;
    };

    // Set initial elapsed time from server
    setElapsedTime(calculateElapsed());

    // Update every second
    const interval = setInterval(() => {
      setElapsedTime(calculateElapsed());
    }, 1000);

    return () => clearInterval(interval);
  }, [clone?.training_status, clone?.training_started_at, clone?.id]);

  const formatTime = (seconds) => {
    if (seconds < 60) return `${seconds}s`;
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const progress = clone?.training_progress || 0;
  const progressPercent = Math.round(progress * 100);
  const remainingTime = Math.max(0, (estimatedTime || 60) - elapsedTime);

  // Determine quality tier based on sample duration
  const getQualityTier = () => {
    const duration = clone?.total_sample_duration || 0;
    if (duration >= 300) return { name: 'Premium', color: 'text-purple-400', bg: 'bg-purple-500/20' };
    if (duration >= 60) return { name: 'Standard', color: 'text-blue-400', bg: 'bg-blue-500/20' };
    return { name: 'Basic', color: 'text-gray-400', bg: 'bg-gray-500/20' };
  };

  const qualityTier = getQualityTier();

  if (clone?.training_status !== 'training') {
    return null;
  }

  return (
    <div className="card border border-yellow-500/30 bg-gradient-to-br from-yellow-500/5 to-orange-500/5">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="relative">
            <div className="w-12 h-12 rounded-full bg-yellow-500/20 flex items-center justify-center">
              <span className="text-2xl animate-pulse">🎯</span>
            </div>
            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-yellow-500 rounded-full animate-ping"></div>
          </div>
          <div>
            <h3 className="font-semibold">Training in Progress</h3>
            <span className={`text-xs px-2 py-0.5 rounded ${qualityTier.bg} ${qualityTier.color}`}>
              {qualityTier.name} Quality
            </span>
          </div>
        </div>
        <button
          onClick={onRefresh}
          className="btn btn-secondary text-sm"
        >
          🔄 Refresh
        </button>
      </div>

      {/* Progress bar */}
      <div className="mb-4">
        <div className="flex justify-between text-sm mb-2">
          <span className="text-gray-400">Progress</span>
          <span className="font-mono">{progressPercent}%</span>
        </div>
        <div className="w-full bg-gray-700 rounded-full h-3 overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-yellow-500 to-orange-500 transition-all duration-500 relative"
            style={{ width: `${progressPercent}%` }}
          >
            <div className="absolute inset-0 bg-white/20 animate-pulse"></div>
          </div>
        </div>
      </div>

      {/* Time info */}
      <div className="grid grid-cols-2 gap-4 mb-4">
        <div className="bg-gray-800/50 rounded-lg p-3 text-center">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Elapsed</p>
          <p className="text-xl font-mono">{formatTime(elapsedTime)}</p>
        </div>
        <div className="bg-gray-800/50 rounded-lg p-3 text-center">
          <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Est. Remaining</p>
          <p className="text-xl font-mono">{formatTime(remainingTime)}</p>
        </div>
      </div>

      {/* Info banner */}
      <div className="bg-green-500/10 border border-green-500/30 rounded-lg p-4 mb-4">
        <div className="flex items-start space-x-3">
          <span className="text-2xl">✅</span>
          <div>
            <p className="font-medium text-green-400 mb-1">You can navigate away!</p>
            <p className="text-sm text-gray-400">
              Training continues in the background. Feel free to close this page, 
              work on other things, or come back later. Your voice clone will be 
              ready when training completes.
            </p>
          </div>
        </div>
      </div>

      {/* Toggle details */}
      <button
        onClick={() => setShowDetails(!showDetails)}
        className="text-sm text-gray-400 hover:text-gray-300 flex items-center"
      >
        {showDetails ? '▼' : '▶'} Training details
      </button>

      {showDetails && (
        <div className="mt-3 pt-3 border-t border-gray-700 space-y-2 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-500">Voice Name</span>
            <span>{clone.name}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Sample Duration</span>
            <span>{clone.total_sample_duration?.toFixed(1)}s</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Sample Count</span>
            <span>{clone.sample_count} files</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-500">Quality Tier</span>
            <span className={qualityTier.color}>{qualityTier.name}</span>
          </div>
        </div>
      )}

      {/* What happens next */}
      <div className="mt-4 pt-4 border-t border-gray-700">
        <p className="text-xs text-gray-500 mb-2">What happens next:</p>
        <div className="space-y-1 text-sm text-gray-400">
          <p>1. ⏳ Training completes automatically</p>
          <p>2. 📧 You'll see the clone in your list as "Ready"</p>
          <p>3. 🎤 Use your cloned voice in Text-to-Speech</p>
        </div>
      </div>
    </div>
  );
}

export default TrainingProgress;
